import * as Yup from 'yup';
import { appConfig, brandConstants } from 'brand-config';

import {
  requiredString,
  requiredIfSectionTownshipRange,
  requiredIfLotBlock,
  isCountyRequired,
  requiredIfStreetAddress
} from 'common/helpers/yup-helpers';
import { isUnderwriter } from 'common/components/farm/locations/risks/risk-types/FO6.utils';

Yup.addMethod(Yup.string, 'requiredIfSectionTownshipRange', requiredIfSectionTownshipRange);
Yup.addMethod(Yup.string, 'requiredIfLotBlock', requiredIfLotBlock);
Yup.addMethod(Yup.string, 'isCountyRequired', isCountyRequired);
Yup.addMethod(Yup.string, 'requiredIfStreetAddress', requiredIfStreetAddress);
Yup.addMethod(Yup.string, 'requiredString', requiredString);

const formatPath = 'legalDescription.format';

export const validationSchema = (session, maxAcres) => {
  const supportedStates = appConfig.supportedStates;
  const isUW = isUnderwriter(session);
  const brandName = brandConstants.SHORT_NAME || 'This brand';
  const allStates = session?.allStates || [];

  return Yup.object().shape({
    legalDescription: Yup.object().shape({
      lot: Yup.string().requiredIfLotBlock('Lot is required', formatPath),
      block: Yup.string().requiredIfLotBlock('Block is required', formatPath),
      section: Yup.string().requiredIfSectionTownshipRange('Section is required', formatPath),
      township: Yup.string().requiredIfSectionTownshipRange('Township is required', formatPath),
      range: Yup.string().requiredIfSectionTownshipRange('Range is required', formatPath),
      townshipCoordinates: Yup.string(),
      rangeCoordinates: Yup.string(),
      format: Yup.string().requiredString('Legal Description Format is required')
    }),
    address: Yup.object().shape({
      address: Yup.string().requiredIfStreetAddress('Address is required', formatPath),
      address2: Yup.string().nullable(),
      city: Yup.string().required('City is required', formatPath),
      state: Yup.string()
        .required('State is required', formatPath)
        .test(
          'supported-state',
          `${brandName} only supports farm locations in ${supportedStates?.join(', ') || 'supported states'}. Out-of-state locations are not allowed.`,
          (value) => {
            // Skip validation if no supported states are configured or user is an underwriter
            if (!supportedStates || supportedStates.length === 0 || isUW) {
              return true;
            }
            // Agents cannot add out-of-state locations
            return !value || supportedStates.includes(value);
          }
        )
        // Each farm location must be in a state the agent is licensed for. Applies to all
        // brands — for Branch this duplicates the NI-level check; for Forreston/Oakwood/CFM
        // (where the NI-level check is relaxed) this is the only licensed-state gate. UW
        // bypass mirrors supported-state. (Issue #30876)
        .test(
          'is-licensed-in-state',
          'You are not licensed in this state. Please reach out to Support if you believe this is a mistake.',
          (value) => {
            if (isUW || !value || allStates.length === 0) {
              return true;
            }
            return allStates.includes(value);
          }
        ),
      zip: Yup.string().required('ZIP is required', formatPath),
      county: Yup.string().isCountyRequired('County is required', formatPath)
    }),
    description: Yup.string().required('Location name is required'),
    numberOfAcres: Yup.number()
      .required('Acreage is required')
      .test('max-acres', `Acreage must not exceed ${maxAcres} for this policy type`, (value) => {
        if (maxAcres == null || value == null) {
          return true;
        }
        return value <= maxAcres;
      })
  });
};
